import * as React from 'react'
import { ClockCounterClockwise, ArrowRight, EyeSlash } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
} from '../core/card'
import { EntityRow, type EntityType } from '../core/entity-glyph'
import { Skeleton } from '../core/skeleton'
import { WidgetMenu, type WidgetMenuItem } from './widget-menu'

/**
 * RecentlyVisitedWidget — the homepage "Recently visited" card: a flat list of
 * the records a user opened most recently, each an {@link EntityRow} (glyph ·
 * name · relative time), separated by dividers and scrolling once the list
 * overflows. Newest first; the consumer supplies the order and the pre-formatted
 * `visitedLabel` ("1h ago", "Yesterday").
 *
 *   <RecentlyVisitedWidget items={recent} onItemClick={openRecord} onHide={hide} />
 *
 * Read/navigate only. Shares the `EntityRow` atom with any other flat record
 * list; the sibling "Upcoming" card ({@link UpcomingWidget}) is the time-based
 * counterpart.
 */

export interface RecentlyVisitedItem {
  /** Stable unique id. */
  id: string
  /** Record type — drives the leading glyph. */
  type: EntityType
  /** Record name. */
  name: string
  /** Pre-formatted relative time, e.g. "1h ago". */
  visitedLabel?: string
  /** Link target for the row; omit to use `onItemClick`. */
  href?: string
}

export interface RecentlyVisitedWidgetProps {
  items: RecentlyVisitedItem[]
  /** Called when a row is clicked (used when an item has no `href`). */
  onItemClick?: (item: RecentlyVisitedItem) => void
  /** Wire the ⋯ menu's "View all" action (navigates to the full history list); hidden when omitted. */
  onViewAll?: () => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  /** Density — `md` (default), `sm`, or `xs` (compact rows, for a tight dashboard column). */
  size?: 'xs' | 'sm' | 'md'
  /** Card heading. */
  title?: React.ReactNode
  /** Text shown when there's no history yet. */
  emptyLabel?: string
  /** Show a loading skeleton (row placeholders) instead of items. */
  loading?: boolean
  className?: string
}

export const RecentlyVisitedWidget = React.forwardRef<
  HTMLDivElement,
  RecentlyVisitedWidgetProps
>(
  (
    {
      items,
      onItemClick,
      onViewAll,
      onHide,
      size = 'md',
      title = 'Recently visited',
      emptyLabel = 'Nothing here yet',
      loading,
      className,
    },
    ref
  ) => {
    if (loading) {
      return (
        <Card
          ref={ref}
          size="sm"
          className={cn('flex max-h-52 w-full max-w-md flex-col', className)}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <ClockCounterClockwise
                aria-hidden="true"
                className="size-4 text-fbui-gray-500"
              />
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col divide-y divide-fbui-gray-100">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-[0.75rem] py-2">
                  <Skeleton className="size-8 shrink-0 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )
    }
    const menuItems: WidgetMenuItem[] = [
      ...(onViewAll
        ? [{ key: 'view-all', label: 'View all', icon: <ArrowRight />, onSelect: onViewAll }]
        : []),
      ...(onHide
        ? [{ key: 'hide', label: 'Hide widget', icon: <EyeSlash />, onSelect: onHide }]
        : []),
    ]
    return (
    <Card ref={ref} size="sm" className={cn('flex max-h-52 w-full max-w-md flex-col', className)}>
      {/* Tighter title→content gap than the Card default (p-4 bottom). */}
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <ClockCounterClockwise
            aria-hidden="true"
            className="size-4 text-fbui-gray-500"
          />
          {title}
        </CardTitle>
        {menuItems.length > 0 && (
          <CardAction>
            <WidgetMenu label="Recently visited options" items={menuItems} />
          </CardAction>
        )}
      </CardHeader>
      {/* Fills the card height when one is set (so the list scrolls); natural
          height otherwise. */}
      <CardContent className="flex min-h-0 flex-1 flex-col">
        {items.length > 0 ? (
          <div className="fbui-scroll flex min-h-0 flex-1 flex-col divide-y divide-fbui-gray-100 overflow-y-auto">
            {items.map((item) => (
              <EntityRow
                key={item.id}
                type={item.type}
                size={size}
                glyphTone="muted"
                title={item.name}
                meta={item.visitedLabel}
                href={item.href}
                onClick={
                  item.href == null && onItemClick
                    ? () => onItemClick(item)
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-fbui-gray-500">
            {emptyLabel}
          </p>
        )}
      </CardContent>
    </Card>
    )
  }
)
RecentlyVisitedWidget.displayName = 'RecentlyVisitedWidget'
